<ng-container *ngIf="layer">
    <div class="section title">
        <button
            [cdkCopyToClipboard]="layer.nameOrDefault"
            class="icon-button"
            title="Copy to clipboard"
        >
            <cds-icon shape="copy-to-clipboard"></cds-icon>
        </button>
        <div class="app-typo-h3 layer-name-container" [title]="layer.nameOrDefault">
            <span class="layer-name-text">{{ layer.nameOrDefault }}</span>
            <button
                *ngIf="layer.name && layer.name !== topLevelSelectionName"
                class="icon-button rename-button"
                (click)="openRenameDialog()"
                title="Rename layer"
            >
                <cds-icon shape="pencil"></cds-icon>
            </button>
        </div>
        <button (click)="close.emit()" class="icon-button icon-big close-button">
            <cds-icon shape="window-close"></cds-icon>
        </button>
    </div>

    <div class="section flex-row col-gap">
        <label class="label">Visibility</label>
        <button class="icon-button" (click)="layer.toggleVisible()">
            <ng-container *ngIf="layer.visible$ | async">
                <cds-icon shape="eye"></cds-icon>
            </ng-container>
            <ng-container *ngIf="!(layer.visible$ | async)">
                <cds-icon shape="eye-hide"></cds-icon>
            </ng-container>
        </button>
    </div>

    <ng-container *ngIf="layer.shaderColor as shaderColor">
        <div class="section flex-row col-gap">
            <label class="label">Color</label>
            <button
                class="color-button"
                ngx-colors-trigger
                [style.background-color]="shaderColor.value$ | async"
                format="hex"
                [hideTextInput]="true"
                colorPickerControls="no-alpha"
                [palette]="colorPalette"
                [ngModel]="shaderColor.value$ | async"
                (ngModelChange)="shaderColor.setValue($event)"
                overlayClassName="color-picker"
            ></button>
        </div>
    </ng-container>

    <ng-container *ngIf="layer.segmentationColor as segmentationColor">
        <div class="section">
            <label class="label">Segmentation Color</label>
            <div class="flex-row segmentation-color-content">
                <cds-radio-group layout="vertical">
                    <cds-radio>
                        <label>Random Colors</label>
                        <input
                            type="radio"
                            name="randomColors"
                            [ngModel]="segmentationColorMode(segmentationColor.value$ | async)"
                            [value]="SegmentationColorModeEnum.RandomSeed"
                            (ngModelChange)="
                                setSegmentationColorMode(SegmentationColorModeEnum.RandomSeed)
                            "
                        />
                    </cds-radio>
                    <cds-radio>
                        <label>Fixed Color</label>
                        <input
                            type="radio"
                            name="fixedColor"
                            [ngModel]="segmentationColorMode(segmentationColor.value$ | async)"
                            [value]="SegmentationColorModeEnum.FixedColor"
                            (ngModelChange)="
                                setSegmentationColorMode(SegmentationColorModeEnum.FixedColor)
                            "
                        />
                    </cds-radio>
                </cds-radio-group>
                <div *ngIf="segmentationColor.value$ | async as color">
                    <button
                        class="color-button"
                        ngx-colors-trigger
                        [style.background-color]="color"
                        format="hex"
                        [hideTextInput]="true"
                        colorPickerControls="no-alpha"
                        [palette]="colorPalette"
                        [ngModel]="color"
                        (ngModelChange)="segmentationColor.setValue($event)"
                        overlayClassName="color-picker"
                    ></button>
                </div>
            </div>
        </div>
    </ng-container>

    <div *ngIf="hasSliderControls" class="section flex-col">
        <form *ngIf="layer.opacityPercent as opacityPercent">
            <cds-form-group>
                <div class="flex-row col-gap">
                    <clr-range-container [clrRangeHasProgress]="true">
                        <label>Opacity</label>
                        <input
                            clrRange
                            name="opacity-range"
                            type="range"
                            max="100"
                            min="0"
                            [ngModel]="opacityPercent.value$ | async"
                            (ngModelChange)="opacityPercent.setValue($event)"
                        />
                    </clr-range-container>
                    <input
                        class="number-input-no-arrow"
                        name="opacity-number"
                        type="number"
                        max="100"
                        min="0"
                        [ngModel]="opacityPercent.value$ | async"
                        (ngModelChange)="opacityPercent.setValue($event)"
                    />
                </div>
            </cds-form-group>
        </form>

        <form *ngIf="layer.opacityUnselectedPercent as opacityUnselectedPercent">
            <cds-form-group layout="vertical">
                <div class="flex-row col-gap">
                    <clr-range-container [clrRangeHasProgress]="true">
                        <label>Opacity (Not selected)</label>
                        <input
                            clrRange
                            name="opacity-range"
                            type="range"
                            max="100"
                            min="0"
                            [ngModel]="opacityUnselectedPercent.value$ | async"
                            (ngModelChange)="opacityUnselectedPercent.setValue($event)"
                        />
                    </clr-range-container>
                    <input
                        class="number-input-no-arrow"
                        name="opacity-number"
                        type="number"
                        max="100"
                        min="0"
                        [ngModel]="opacityUnselectedPercent.value$ | async"
                        (ngModelChange)="opacityUnselectedPercent.setValue($event)"
                    />
                </div>
            </cds-form-group>
        </form>

        <form *ngIf="layer.saturationPercent as saturationPercent">
            <cds-form-group layout="vertical">
                <div class="flex-row col-gap">
                    <clr-range-container [clrRangeHasProgress]="true">
                        <label>Saturation</label>
                        <input
                            clrRange
                            name="saturation-range"
                            type="range"
                            max="100"
                            min="0"
                            [ngModel]="saturationPercent.value$ | async"
                            (ngModelChange)="saturationPercent.setValue($event)"
                        />
                    </clr-range-container>
                    <input
                        class="number-input-no-arrow"
                        name="saturation-number"
                        type="number"
                        max="100"
                        min="0"
                        [ngModel]="saturationPercent.value$ | async"
                        (ngModelChange)="saturationPercent.setValue($event)"
                    />
                </div>
            </cds-form-group>
        </form>
        <form *ngIf="layer.normalize as normalize">
            <cds-form-group layout="vertical" gap="none">
                <div class="flex-col-gap0">
                    <div class="flex-row">
                        <label>
                            Normalize
                            <button
                                class="icon-button reset-btn"
                                (click)="resetNormalize()"
                                onclick="this.parentNode.parentNode.children[1].children[0].style.setProperty('--value-a',0); this.parentNode.parentNode.children[1].children[0].style.setProperty('--text-value-a', JSON.stringify(0)); this.parentNode.parentNode.children[1].children[0].style.setProperty('--value-b',255); this.parentNode.parentNode.children[1].children[0].style.setProperty('--text-value-b', JSON.stringify(255));"
                            >
                                <img src="assets/icons/reset.svg" />
                            </button>
                        </label>
                        <label id="autoComputeBtnContainer">
                            <button
                                class="icon-button"
                                id="autoComputeBtn"
                                (click)="autoComputeRange(1)"
                            >
                                1-99%
                            </button>
                            <button
                                class="icon-button"
                                id="autoComputeBtn"
                                (click)="autoComputeRange(5)"
                            >
                                5-95%
                            </button>
                        </label>
                    </div>
                    <canvas class="canvas" id="canvas-histogram"></canvas>
                    <div
                        class="range-slider flat"
                        data-ticks-position="top"
                        [style]="
                            '--min: ' +
                            histogramMinValue +
                            '; --max: ' +
                            histogramMaxValue +
                            '; --value-a: ' +
                            normalizeRange[0] +
                            '; --value-b: ' +
                            normalizeRange[1]
                        "
                    >
                        <input
                            name="normalize-lower"
                            type="range"
                            [min]="histogramMinValue"
                            [max]="histogramMaxValue"
                            value="(normalize.value$ | async)?.range[0]"
                            oninput="this.parentNode.style.setProperty('--value-a',this.value);"
                            [ngModel]="normalizeRange[0]"
                            (ngModelChange)="setNormalizeRange($event, null)"
                        />
                        <output></output>
                        <input
                            name="normalize-upper"
                            type="range"
                            [min]="histogramMinValue"
                            [max]="histogramMaxValue"
                            value="(normalize.value$ | async)?.range[1]"
                            oninput="this.parentNode.style.setProperty('--value-b',this.value);"
                            [ngModel]="normalizeRange[1]"
                            (ngModelChange)="setNormalizeRange(null, $event)"
                        />
                        <output></output>
                        <div class="range-slider__progress"></div>
                    </div>
                </div>
            </cds-form-group>
        </form>

        <button
            class="icon-button"
            *ngIf="layer.name && layer.name !== topLevelSelectionName"
            (click)="openDeleteDialog()"
        >
            <cds-icon shape="trash"></cds-icon>
        </button>
    </div>
</ng-container>
